import { Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';

import {
  ACTIVITIES_ENDPOINTS,
} from '../api/api-endpoints';
import { RestApiService } from '../api/rest-api.service';
import { TimezoneService } from './timezone.service';
import { formatString } from '../utils/string.utils';

export type ActivityType = 'task' | 'call' | 'meeting' | 'email' | 'follow_up' | 'campaign' | 'shift' | 'reminder';
export type ActivityStatus = 'draft' | 'scheduled' | 'in_progress' | 'done' | 'cancelled' | 'missed';
export type ParticipantRole = 'owner' | 'assignee' | 'attendee' | 'watcher';
export type ParticipantResponseStatus = 'pending' | 'accepted' | 'declined' | 'tentative';

export interface ActivityParticipant {
  id: string;
  activity_id: string;
  user_id: string | null;
  employee_id: string | null;
  response_status: ParticipantResponseStatus;
  participant_role: ParticipantRole;
  created_at: string;
  updated_at: string;
}

export interface ActivityLink {
  id: string;
  activity_id: string;
  app_name: string;
  record_type: string;
  record_id: string;
  relationship_type: string;
  created_at: string;
}

export interface ActivityRecord {
  id: string;
  organisation_id: string;
  title: string;
  description: string | null;
  activity_type: ActivityType;
  status: ActivityStatus;
  start_time_utc: string | null;
  end_time_utc: string | null;
  timezone: string | null;
  all_day: boolean;
  recurrence_rule: string | null;
  location: string | null;
  created_by: string;
  updated_by: string;
  created_at: string;
  updated_at: string;
  participants: ActivityParticipant[];
  links: ActivityLink[];
}

export interface ActivityListResult {
  page: number;
  limit: number;
  total_records: number;
  total_pages: number;
  has_next_page: boolean;
  has_previous_page: boolean;
  activities: ActivityRecord[];
}

export interface ActivityListFilters {
  activity_type?: ActivityType;
  status?: ActivityStatus;
  participant_user_id?: string;
  start_time_utc_from?: string;
  end_time_utc_to?: string;
  linked_app_name?: string;
  linked_record_type?: string;
  linked_record_id?: string;
  search?: string;
  page?: number;
  limit?: number;
}

export interface ActivityUpsertInput {
  title?: string;
  description?: string | null;
  activity_type?: ActivityType;
  status?: ActivityStatus;
  start_time_utc?: string | null;
  end_time_utc?: string | null;
  start_time_local?: string | null;
  end_time_local?: string | null;
  timezone?: string | null;
  all_day?: boolean;
  recurrence_rule?: string | null;
  location?: string | null;
}

export interface ActivityCreateInput extends ActivityUpsertInput {
  title: string;
  activity_type: ActivityType;
  participants?: Array<{
    user_id?: string | null;
    employee_id?: string | null;
    response_status?: ParticipantResponseStatus;
    participant_role?: ParticipantRole;
  }>;
  links?: Array<{
    app_name: string;
    record_type: string;
    record_id: string;
    relationship_type: string;
  }>;
}

@Injectable({
  providedIn: 'root',
})
export class ActivityService {
  constructor(
    private readonly restApi: RestApiService,
    private readonly timezoneService: TimezoneService
  ) {}

  async listActivities(filters: ActivityListFilters = {}): Promise<ActivityListResult> {
    const query = new URLSearchParams();

    const append = (key: string, value: unknown) => {
      const text = this.cleanText(value);
      if (text) {
        query.set(key, text);
      }
    };

    append('activity_type', filters.activity_type);
    append('status', filters.status);
    append('participant_user_id', filters.participant_user_id);
    append('start_time_utc_from', filters.start_time_utc_from);
    append('end_time_utc_to', filters.end_time_utc_to);
    append('linked_app_name', filters.linked_app_name);
    append('linked_record_type', filters.linked_record_type);
    append('linked_record_id', filters.linked_record_id);
    append('search', filters.search);
    append('page', filters.page);
    append('limit', filters.limit);

    const suffix = query.toString() ? `?${query.toString()}` : '';
    const response: any = await firstValueFrom(this.restApi.get(`${ACTIVITIES_ENDPOINTS.list}${suffix}`));

    return {
      page: this.toNumber(response?.page, 1),
      limit: this.toNumber(response?.limit, 20),
      total_records: this.toNumber(response?.total_records, 0),
      total_pages: this.toNumber(response?.total_pages, 0),
      has_next_page: !!response?.has_next_page,
      has_previous_page: !!response?.has_previous_page,
      activities: this.mapActivities(response?.activities),
    };
  }

  async getActivity(activityId: string): Promise<ActivityRecord | null> {
    const endpoint = formatString(ACTIVITIES_ENDPOINTS.by_id, activityId);
    const response: any = await firstValueFrom(this.restApi.get(endpoint));
    return this.mapActivity(response);
  }

  async createActivity(input: ActivityCreateInput): Promise<ActivityRecord | null> {
    const payload = this.buildUpsertPayload(input);
    const response: any = await firstValueFrom(this.restApi.post(ACTIVITIES_ENDPOINTS.create, {
      ...payload,
      title: input.title,
      activity_type: input.activity_type,
      participants: Array.isArray(input.participants) ? input.participants : undefined,
      links: Array.isArray(input.links) ? input.links : undefined,
    }));

    return this.mapActivity(response?.activity ?? response);
  }

  async updateActivity(activityId: string, input: ActivityUpsertInput): Promise<ActivityRecord | null> {
    const endpoint = formatString(ACTIVITIES_ENDPOINTS.by_id, activityId);
    const payload = this.buildUpsertPayload(input);

    const response: any = await firstValueFrom(this.restApi.put(endpoint, payload));
    return this.mapActivity(response?.activity ?? response);
  }

  async cancelActivity(activityId: string): Promise<ActivityRecord | null> {
    const endpoint = formatString(ACTIVITIES_ENDPOINTS.cancel, activityId);
    const response: any = await firstValueFrom(this.restApi.post(endpoint, {}));
    return this.mapActivity(response?.activity ?? response);
  }

  async markDone(activityId: string): Promise<ActivityRecord | null> {
    const endpoint = formatString(ACTIVITIES_ENDPOINTS.done, activityId);
    const response: any = await firstValueFrom(this.restApi.post(endpoint, {}));
    return this.mapActivity(response?.activity ?? response);
  }

  async deleteDraft(activityId: string): Promise<boolean> {
    const endpoint = formatString(ACTIVITIES_ENDPOINTS.by_id, activityId);
    await firstValueFrom(this.restApi.delete(endpoint));
    return true;
  }

  async getCalendarRange(startTimeUtcFrom: string, endTimeUtcTo: string, participantUserId?: string): Promise<ActivityRecord[]> {
    const query = new URLSearchParams();
    query.set('start_time_utc_from', this.cleanText(startTimeUtcFrom) || '');
    query.set('end_time_utc_to', this.cleanText(endTimeUtcTo) || '');

    const participant = this.cleanText(participantUserId);
    if (participant) {
      query.set('participant_user_id', participant);
    }

    const response: any = await firstValueFrom(
      this.restApi.get(`${ACTIVITIES_ENDPOINTS.calendar_range}?${query.toString()}`)
    );

    return this.mapActivities(response);
  }

  async addParticipant(
    activityId: string,
    payload: {
      user_id?: string | null;
      employee_id?: string | null;
      response_status?: ParticipantResponseStatus;
      participant_role?: ParticipantRole;
    }
  ): Promise<ActivityParticipant | null> {
    const endpoint = formatString(ACTIVITIES_ENDPOINTS.participants, activityId);
    const response: any = await firstValueFrom(this.restApi.post(endpoint, payload));
    return this.mapParticipant(response?.participant ?? response);
  }

  async removeParticipant(activityId: string, participantId: string): Promise<boolean> {
    const endpoint = formatString(ACTIVITIES_ENDPOINTS.participant_by_id, activityId, participantId);
    await firstValueFrom(this.restApi.delete(endpoint));
    return true;
  }

  async updateParticipant(
    activityId: string,
    participantId: string,
    payload: {
      response_status: ParticipantResponseStatus;
      participant_role?: ParticipantRole;
    }
  ): Promise<ActivityParticipant | null> {
    const endpoint = formatString(ACTIVITIES_ENDPOINTS.participant_by_id, activityId, participantId);
    const response: any = await firstValueFrom(this.restApi.put(endpoint, payload));
    return this.mapParticipant(response?.participant ?? response);
  }

  async linkActivity(
    activityId: string,
    payload: {
      app_name: string;
      record_type: string;
      record_id: string;
      relationship_type: string;
    }
  ): Promise<ActivityLink | null> {
    const endpoint = formatString(ACTIVITIES_ENDPOINTS.links, activityId);
    const response: any = await firstValueFrom(this.restApi.post(endpoint, payload));
    return this.mapLink(response?.link ?? response);
  }

  async unlinkActivity(activityId: string, linkId: string): Promise<boolean> {
    const endpoint = formatString(ACTIVITIES_ENDPOINTS.link_by_id, activityId, linkId);
    await firstValueFrom(this.restApi.delete(endpoint));
    return true;
  }

  private buildUpsertPayload(input: ActivityUpsertInput): Record<string, unknown> {
    const payload: Record<string, unknown> = {};

    const timezone = this.timezoneService.validateTimezone(input.timezone);

    const startUtc =
      this.cleanText(input.start_time_utc) ||
      this.toUtc(input.start_time_local, timezone);
    const endUtc =
      this.cleanText(input.end_time_utc) ||
      this.toUtc(input.end_time_local, timezone);

    this.assign(payload, 'title', input.title);
    this.assign(payload, 'description', input.description);
    this.assign(payload, 'activity_type', input.activity_type);
    this.assign(payload, 'status', input.status);
    this.assign(payload, 'start_time_utc', startUtc);
    this.assign(payload, 'end_time_utc', endUtc);
    this.assign(payload, 'timezone', timezone);
    this.assign(payload, 'all_day', input.all_day);
    this.assign(payload, 'recurrence_rule', input.recurrence_rule);
    this.assign(payload, 'location', input.location);

    return payload;
  }

  private assign(target: Record<string, unknown>, key: string, value: unknown): void {
    if (typeof value === 'boolean') {
      target[key] = value;
      return;
    }

    const text = this.cleanText(value);
    if (text !== null) {
      target[key] = text;
    }
  }

  private toUtc(localValue: unknown, timezone: string): string | null {
    const local = this.cleanText(localValue);
    if (!local) {
      return null;
    }

    const utc = this.timezoneService.localInputToUtcIso(local, timezone);
    return this.cleanText(utc);
  }

  private mapActivities(value: unknown): ActivityRecord[] {
    if (!Array.isArray(value)) {
      return [];
    }

    return value
      .map((item) => this.mapActivity(item))
      .filter((item): item is ActivityRecord => !!item);
  }

  private mapActivity(value: any): ActivityRecord | null {
    if (!value || typeof value !== 'object') {
      return null;
    }

    const id = this.cleanText(value.id);
    if (!id) {
      return null;
    }

    return {
      id,
      organisation_id: this.cleanText(value.organisation_id) || '',
      title: this.cleanText(value.title) || 'Untitled Activity',
      description: this.cleanText(value.description),
      activity_type: this.cleanActivityType(value.activity_type),
      status: this.cleanStatus(value.status),
      start_time_utc: this.cleanText(value.start_time_utc),
      end_time_utc: this.cleanText(value.end_time_utc),
      timezone: this.cleanText(value.timezone),
      all_day: !!value.all_day,
      recurrence_rule: this.cleanText(value.recurrence_rule),
      location: this.cleanText(value.location),
      created_by: this.cleanText(value.created_by) || '',
      updated_by: this.cleanText(value.updated_by) || '',
      created_at: this.cleanText(value.created_at) || '',
      updated_at: this.cleanText(value.updated_at) || '',
      participants: Array.isArray(value.participants)
        ? value.participants
            .map((item: unknown) => this.mapParticipant(item))
            .filter((item: ActivityParticipant | null): item is ActivityParticipant => !!item)
        : [],
      links: Array.isArray(value.links)
        ? value.links
            .map((item: unknown) => this.mapLink(item))
            .filter((item: ActivityLink | null): item is ActivityLink => !!item)
        : [],
    };
  }

  private mapParticipant(value: any): ActivityParticipant | null {
    if (!value || typeof value !== 'object') {
      return null;
    }

    const id = this.cleanText(value.id);
    if (!id) {
      return null;
    }

    return {
      id,
      activity_id: this.cleanText(value.activity_id) || '',
      user_id: this.cleanText(value.user_id),
      employee_id: this.cleanText(value.employee_id),
      response_status: this.cleanParticipantResponseStatus(value.response_status),
      participant_role: this.cleanParticipantRole(value.participant_role),
      created_at: this.cleanText(value.created_at) || '',
      updated_at: this.cleanText(value.updated_at) || '',
    };
  }

  private mapLink(value: any): ActivityLink | null {
    if (!value || typeof value !== 'object') {
      return null;
    }

    const id = this.cleanText(value.id);
    if (!id) {
      return null;
    }

    return {
      id,
      activity_id: this.cleanText(value.activity_id) || '',
      app_name: this.cleanText(value.app_name) || '',
      record_type: this.cleanText(value.record_type) || '',
      record_id: this.cleanText(value.record_id) || '',
      relationship_type: this.cleanText(value.relationship_type) || '',
      created_at: this.cleanText(value.created_at) || '',
    };
  }

  private cleanActivityType(value: unknown): ActivityType {
    const normalized = (this.cleanText(value) || 'task').toLowerCase();
    if (normalized === 'call' || normalized === 'meeting' || normalized === 'email' || normalized === 'follow_up' || normalized === 'campaign' || normalized === 'shift' || normalized === 'reminder') {
      return normalized;
    }

    return 'task';
  }

  private cleanStatus(value: unknown): ActivityStatus {
    const normalized = (this.cleanText(value) || 'draft').toLowerCase();
    if (normalized === 'scheduled' || normalized === 'in_progress' || normalized === 'done' || normalized === 'cancelled' || normalized === 'missed') {
      return normalized;
    }

    return 'draft';
  }

  private cleanParticipantRole(value: unknown): ParticipantRole {
    const normalized = (this.cleanText(value) || 'attendee').toLowerCase();
    if (normalized === 'owner' || normalized === 'assignee' || normalized === 'watcher') {
      return normalized;
    }

    return 'attendee';
  }

  private cleanParticipantResponseStatus(value: unknown): ParticipantResponseStatus {
    const normalized = (this.cleanText(value) || 'pending').toLowerCase();
    if (normalized === 'accepted' || normalized === 'declined' || normalized === 'tentative') {
      return normalized;
    }

    return 'pending';
  }

  private cleanText(value: unknown): string | null {
    if (typeof value !== 'string') {
      return null;
    }

    const trimmed = value.trim();
    return trimmed.length > 0 ? trimmed : null;
  }

  private toNumber(value: unknown, fallback: number): number {
    if (typeof value === 'number' && Number.isFinite(value)) {
      return Math.max(0, Math.trunc(value));
    }

    return fallback;
  }
}
